import { get, patch, put } from './apiClient';

/**
 * Boolean flags on an AppDefinition ObjectDef that may be edited per tenant.
 * Mirrors AppDefinition's ObjectFlagsUpdateDto allow-list.
 */
export const OBJECT_TYPE_FLAGS = [
    'inMenu',
    'inKeywordSearch',
    'inAdvancedSearch',
    'inRecycleBin',
    'inDesigner',
    'inArchive',
    'inPermissions',
    'hasWorkflow',
    'hasConversations',
    'isPortalUser',
    'enableWordTemplates',
    'dedicatedService',
] as const;

export type ObjectTypeFlag = typeof OBJECT_TYPE_FLAGS[number];
export type ObjectTypeFlags = Partial<Record<ObjectTypeFlag, boolean>>;

/**
 * Flags shown as columns in the admin grid.
 *
 * `enableWordTemplates` and `dedicatedService` are intentionally omitted:
 *  - enableWordTemplates is force-set by AppDefinition whenever a layout containing a
 *    Word-enabled FileUpload is saved, and is absent from the projection used by that
 *    save — so a value set here does not reliably survive.
 *  - dedicatedService changes permission/routing semantics rather than presentation, and
 *    overlaps confusingly with the tenant's own dedicatedDeploymentObjectTypes setting.
 * Both remain accepted by the API for callers that genuinely need them.
 */
export const EDITABLE_FLAGS: { key: ObjectTypeFlag; label: string; hint: string }[] = [
    { key: 'inMenu',           label: 'In menu',      hint: 'Appears in the main navigation menu' },
    { key: 'inKeywordSearch',  label: 'Keyword',      hint: 'Included in global keyword search' },
    { key: 'inAdvancedSearch', label: 'Advanced',     hint: 'Selectable in advanced search' },
    { key: 'inRecycleBin',     label: 'Recycle bin',  hint: 'Has a tab in the recycle bin' },
    { key: 'inDesigner',       label: 'Designer',     hint: 'Selectable in the layout designer' },
    { key: 'inArchive',        label: 'Archive',      hint: 'Has an archive tab and per-row archive action' },
    { key: 'inPermissions',    label: 'Permissions',  hint: 'Shown in role and permission trees' },
    { key: 'hasWorkflow',      label: 'Workflow',     hint: 'Renders the workflow bar on the form' },
    { key: 'hasConversations', label: 'Conversations', hint: 'Documents of this type carry conversation threads' },
    { key: 'isPortalUser',     label: 'Portal user',  hint: 'Documents of this type represent portal users' },
];

export interface ObjectType {
    type: string;
    label?: string;
    icon?: string;
    color?: string;
    systemObject?: boolean;
    menuposition?: number;
    inMenu?: boolean;
    inKeywordSearch?: boolean;
    inAdvancedSearch?: boolean;
    inRecycleBin?: boolean;
    inDesigner?: boolean;
    inArchive?: boolean;
    inPermissions?: boolean;
    hasWorkflow?: boolean;
    hasConversations?: boolean;
    isPortalUser?: boolean;
    enableWordTemplates?: boolean;
    dedicatedService?: boolean;
    /** Field paths whose user value joins every conversation on documents of this type. */
    conversationParticipants?: ConversationParticipant[];
}

/** One entry of ObjectDef.conversationParticipants. */
export interface ConversationParticipant {
    /** Dot path of a user-typed field, e.g. `summary.generalInformation.contractOwner` or `system.createUser`. */
    path: string;
    /** Free-text role label shown on the thread; server default is "Member". Never "InstitutionContact". */
    role?: string;
    /** May close / manage the thread. */
    canManage?: boolean;
}

/** A user-typed field of an object type (layout field or system field) selectable as a participant. */
export interface UserField {
    path: string;
    label: string;
    isSystem: boolean;
}

/** Reserved role name; the BFF rejects it with 400 so it is blocked client-side too. */
export const RESERVED_PARTICIPANT_ROLE = 'InstitutionContact';
/** Maximum participant entries the BFF accepts per object type. */
export const MAX_PARTICIPANTS = 50;

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

/** List all object type definitions for a tenant. */
export const getObjectTypes = (tenantId: string): Promise<ApiResponse<ObjectType[]>> =>
    get<ApiResponse<ObjectType[]>>(`/object-types/${encodeURIComponent(tenantId)}`);

/** Toggle one or more allow-listed boolean flags on an object type. */
export const updateObjectTypeFlags = (
    tenantId: string,
    objectType: string,
    flags: ObjectTypeFlags
): Promise<ApiResponse<unknown>> =>
    patch<ApiResponse<unknown>>(
        `/object-types/${encodeURIComponent(tenantId)}/${encodeURIComponent(objectType)}/flags`,
        flags
    );

/** List the user-typed fields (layout + system) of an object type — options for the participant path picker. */
export const getUserFields = (tenantId: string, objectType: string): Promise<ApiResponse<UserField[]>> =>
    get<ApiResponse<UserField[]>>(
        `/object-types/${encodeURIComponent(tenantId)}/${encodeURIComponent(objectType)}/user-fields`
    );

/** Replace the conversation participant list of an object type. An empty array clears it. */
export const updateConversationParticipants = (
    tenantId: string,
    objectType: string,
    participants: ConversationParticipant[]
): Promise<ApiResponse<unknown>> =>
    put<ApiResponse<unknown>>(
        `/object-types/${encodeURIComponent(tenantId)}/${encodeURIComponent(objectType)}/conversation-participants`,
        { participants }
    );
